import { Ionicons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';
import { usePathname, useRouter } from 'expo-router';
import { useMemo, type ReactNode } from 'react';
import { Platform, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { useNeedsAction } from '@/contexts/NeedsActionContext';
import { useTheme } from '@/contexts/ThemeContext';
import type { AppColors } from '@/constants/theme';

export const WEB_WIDE_BREAKPOINT = 900;
const SIDEBAR_WIDTH = 240;
const CONTENT_MAX_WIDTH = 860;

const NAV_ITEMS = [
  { route: '/',              icon: 'home'           },
  { route: '/agreements',    icon: 'document-text', badgeKey: 'agreements' },
  { route: '/offers',        icon: 'mail-unread',    badgeKey: 'offers'     },
  { route: '/circle',        icon: 'people',         badgeKey: 'connections' },
  { route: '/professionals', icon: 'briefcase'      },
  { route: '/wallet',        icon: 'wallet'         },
] as const;

const SECONDARY_NAV_ITEMS = [
  { route: '/profile',  icon: 'person'   },
  { route: '/settings', icon: 'settings' },
] as const;

export function useIsWideWeb() {
  const { width } = useWindowDimensions();
  return Platform.OS === 'web' && width >= WEB_WIDE_BREAKPOINT;
}

export function WebShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const router = useRouter();
  const pathname = usePathname();
  const { breakdown } = useNeedsAction();
  const isWideWeb = useIsWideWeb();

  const NAV_LABELS: Record<string, string> = {
    '/': t('home.tab_label'),
    '/agreements': t('agreements.tab_label'),
    '/offers': t('offers.incoming_title'),
    '/circle': t('circle.title'),
    '/professionals': t('professionals.tab_browse'),
    '/wallet': t('data.wallet'),
    '/profile': t('profile.title'),
    '/settings': t('settings.title'),
  };

  const badgeCountFor = (badgeKey?: string) => {
    if (badgeKey === 'agreements') return breakdown.agreements;
    if (badgeKey === 'offers') return breakdown.offers;
    if (badgeKey === 'connections') return breakdown.connections;
    return 0;
  };

  if (!isWideWeb) {
    return <>{children}</>;
  }

  const renderItem = (item: { route: string; icon: string; badgeKey?: string }) => {
    const active = pathname === item.route;
    const badgeCount = badgeCountFor(item.badgeKey);
    return (
      <Pressable
        key={item.route}
        style={[styles.navItem, active && styles.navItemActive]}
        onPress={() => !active && router.replace(item.route as never)}
        accessibilityRole="tab"
        accessibilityState={{ selected: active }}
      >
        <Ionicons
          name={(active ? item.icon : `${item.icon}-outline`) as keyof typeof Ionicons.glyphMap}
          size={20}
          color={active ? colors.primary : colors.textMuted}
        />
        <Text style={[styles.navLabel, active && styles.navLabelActive]}>
          {NAV_LABELS[item.route]}
        </Text>
        {badgeCount > 0 && (
          <View style={styles.badge}>
            <Text style={styles.badgeText}>{badgeCount > 9 ? '9+' : badgeCount}</Text>
          </View>
        )}
      </Pressable>
    );
  };

  return (
    <View style={styles.shell}>
      <View style={styles.sidebar}>
        <Text style={styles.logo}>Χειραψία</Text>
        <View style={styles.navList}>
          {NAV_ITEMS.map(renderItem)}
        </View>
        <View style={styles.navDivider} />
        <View style={styles.navList}>
          {SECONDARY_NAV_ITEMS.map(renderItem)}
        </View>
      </View>
      <View style={styles.contentArea}>
        <View style={styles.content}>{children}</View>
      </View>
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    shell: {
      flex: 1,
      flexDirection: 'row',
      backgroundColor: colors.background,
      ...(Platform.OS === 'web' ? ({ minHeight: '100vh' } as object) : {}),
    },
    sidebar: {
      width: SIDEBAR_WIDTH,
      borderRightWidth: StyleSheet.hairlineWidth,
      borderRightColor: colors.border,
      paddingTop: 32,
      paddingHorizontal: 16,
      gap: 24,
    },
    logo: {
      color: colors.primary,
      fontSize: 20,
      fontWeight: '800',
      paddingHorizontal: 12,
    },
    navList: {
      gap: 4,
    },
    navDivider: {
      height: StyleSheet.hairlineWidth,
      backgroundColor: colors.border,
      marginVertical: 4,
    },
    navItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 10,
    },
    navItemActive: {
      backgroundColor: colors.surface,
    },
    navLabel: {
      color: colors.textMuted,
      fontSize: 14,
      fontWeight: '600',
    },
    navLabelActive: {
      color: colors.primary,
    },
    badge: {
      marginLeft: 'auto',
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      backgroundColor: '#EF4444',
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
    },
    badgeText: {
      color: '#FFFFFF',
      fontSize: 10,
      fontWeight: '800',
    },
    contentArea: {
      flex: 1,
      alignItems: 'center',
    },
    content: {
      flex: 1,
      width: '100%',
      maxWidth: CONTENT_MAX_WIDTH,
    },
  });
}
